// Selection primitives shared across event search, aggregations, and bulk tables.

import { META_FIELDS } from '@product/stores/dataSources'

const META_FIELD_NAMES = new Set(META_FIELDS.map(f => f.name))

// Filter operators (how multiple values are combined)
export const FILTER_OPERATORS = {
  ANYOF: 'anyof',  // OR - match any of the values
  ALL: 'all',      // AND - match all values
  NOT: 'not'       // NOT - exclude all values
}

// Selection item modes.
//   FIELD_VALUE — Kibana-style field:value scalar match (the default).
//   CONTAINS    — field-scoped: the field's value (array/object) contains this
//                 scalar leaf. This is the per-token filter (e.g. one anon
//                 provider); multiple CONTAINS items under OR = "any of these".
//   PARITY      — whole-cell exact match: the field's value equals this cell's
//                 value (deep equality for structured values). This is the naive
//                 column +/- .
//   VALUE_ONLY  — match the value in any field (right-click menu option).
export const QUEUE_MODES = {
  FIELD_VALUE: 'field-value',
  CONTAINS: 'contains',
  PARITY: 'parity',
  VALUE_ONLY: 'value-only'
}

// Canonical key for exact-parity comparison: structured values compare by their
// stable JSON, scalars by their string form.
export function parityKey(value) {
  if (value !== null && value !== undefined && typeof value === 'object') {
    return JSON.stringify(value)
  }
  return String(value)
}

// IPv4 / IPv6 regexes used by the "Open IPs" action
export const IPV4_RE = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/
export const IPV6_RE = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}$/

export function generateFilterId() {
  return `filter-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

// First scalar leaf of a value — used to make object/array cells filterable:
// arrays yield their first scalar element, objects their first scalar property
// (depth-first). Returns undefined when there is no scalar anywhere.
export function firstScalarLeaf(node) {
  if (node === null || node === undefined) return undefined
  if (typeof node !== 'object') return node
  const children = Array.isArray(node) ? node : Object.values(node)
  for (const child of children) {
    const leaf = firstScalarLeaf(child)
    if (leaf !== undefined && leaf !== null) return leaf
  }
  return undefined
}

// Every scalar leaf of a value (depth-first). A scalar returns [self]; an
// array/object returns all of its nested scalars in order. Used to render one
// chip per leaf for structured cells and to match a field-scoped filter against
// any leaf of that field's value.
export function scalarLeaves(node) {
  if (node === null || node === undefined) return []
  if (typeof node !== 'object') return [node]
  const children = Array.isArray(node) ? node : Object.values(node)
  const out = []
  for (const child of children) out.push(...scalarLeaves(child))
  return out
}

// A value is "empty" for filtering when it carries no filterable scalar
// content: null/undefined/'' , an empty array, or a structure whose leaves are
// all empty (e.g. {}, [{}], [ '' ]). This is what lets an anon-provider column
// with an empty object still be filtered out as "(empty)".
export function isEmptyValue(value) {
  if (value === null || value === undefined || value === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  if (typeof value === 'object') {
    return scalarLeaves(value).every(l => l === null || l === undefined || l === '')
  }
  return false
}

// Does a row match one selection item? Row shapes differ per table, so the host
// supplies the accessors: `getValue(field)` returns the row's value at a field;
// `containsValue(target)` deep-scans the whole row (only needed for value-only
// items). Field-value items match a scalar directly, or — for structured
// values (arrays/objects) — against any scalar leaf of that field, keeping the
// match field-scoped rather than a global scan.
function itemMatchesRow(item, getValue, containsValue) {
  if (item.empty) return isEmptyValue(getValue(item.field))
  const target = String(item.rawValue)
  if (item.mode === QUEUE_MODES.PARITY) {
    return parityKey(getValue(item.field)) === target
  }
  if (item.mode === QUEUE_MODES.CONTAINS) {
    return scalarLeaves(getValue(item.field)).some(l => String(l) === target)
  }
  if (item.mode === QUEUE_MODES.VALUE_ONLY) {
    return containsValue ? containsValue(item.rawValue) : false
  }
  // FIELD_VALUE: scalar equality, with array containment as a fallback so
  // multi-valued scalar fields (e.g. tag lists) still match a single tag.
  const value = getValue(item.field)
  if (value !== null && value !== undefined && typeof value === 'object') {
    return scalarLeaves(value).some(l => String(l) === target)
  }
  return String(value) === target
}

// Whole-row deep scan for a target value — backs value-only ("in any field")
// matching. Row shapes vary, so this walks the entire row object.
export function deepContainsValue(row, target) {
  const t = String(target)
  const stack = [row]
  while (stack.length) {
    const node = stack.pop()
    if (node === null || node === undefined) continue
    if (typeof node === 'object') {
      if (Array.isArray(node)) {
        for (const v of node) stack.push(v)
      } else {
        for (const k in node) stack.push(node[k])
      }
      continue
    }
    if (String(node) === t) return true
  }
  return false
}

// Combine an applied filter's items under its operator.
export function matchFilter(filter, getValue, containsValue) {
  const test = item => itemMatchesRow(item, getValue, containsValue)
  if (filter.operator === FILTER_OPERATORS.ALL) return filter.items.every(test)
  if (filter.operator === FILTER_OPERATORS.NOT) return !filter.items.some(test)
  return filter.items.some(test)
}

// Canonical selection-item factory. One place owns the item shape so the
// selection composable, hover +/- buttons, and context menu all agree.
// Shape: { id, field, value, rawValue, mode, empty, valueKey }
// `empty` marks a missing/empty cell — it filters on field existence rather
// than a concrete value.
export function makeSelectionItem(field, rawValue, formattedValue, mode = QUEUE_MODES.FIELD_VALUE, empty = false) {
  return {
    id: generateFilterId(),
    field,
    value: formattedValue ?? String(rawValue),
    rawValue,
    mode,
    empty,
    valueKey: empty ? `${field}:__empty__` : `${field}:${JSON.stringify(rawValue)}`,
  }
}

// Build a Lucene fragment for one queued item. FIELD_VALUE items render as
// value-only when the field isn't present in the flattened source field
// mappings and isn't a META_FIELD — the name lives in a different namespace
// (e.g. IP Bulk enrichment keys like `location.country_iso`) and a
// field-prefixed query would either miss everything or match the wrong
// column. META_FIELDS (`ip`, `host`, `hash`, ...) are explicit cross-
// namespace aliases that expand server-side, so they stay field-prefixed.
//
// `fieldMappings` is `dataSourcesStore.fieldMappings` — { logType: [fields] }
// where fields are strings or { name, ... } objects.
export function buildQueryFragment(item, fieldMappings) {
  if (item.empty) return `(NOT ${item.field}:*)`
  // PARITY has no faithful Lucene form (it's a whole-structure equality); fall
  // back to a value-only match on its display string for cross-navigation.
  if (item.mode === QUEUE_MODES.PARITY) {
    return `"${String(item.value).replace(/"/g, '\\"')}"`
  }
  const escaped = String(item.rawValue).replace(/"/g, '\\"')
  // VALUE_ONLY stays fieldless; FIELD_VALUE and CONTAINS are field-scoped (ES
  // matches a bare value against every element of an array field).
  if (item.mode === QUEUE_MODES.VALUE_ONLY) return `"${escaped}"`
  const knownField = META_FIELD_NAMES.has(item.field)
    || fieldInMappings(item.field, fieldMappings)
  return knownField ? `${item.field}:"${escaped}"` : `"${escaped}"`
}

function fieldInMappings(name, mappings) {
  if (!mappings) return false
  for (const fields of Object.values(mappings)) {
    if (!Array.isArray(fields)) continue
    for (const f of fields) {
      const fname = typeof f === 'string' ? f : f?.name
      if (fname === name) return true
    }
  }
  return false
}
